import {useCallback} from "react";
import {create} from "zustand";

export interface CacheValueFn<D extends Record<string, any>> {
    (): D; // no args, return entire cache
    <T extends keyof D>(field: T): D[T] | undefined; // no default
    <T extends keyof D>(field: T, def: D[T]): D[T]; // with default
}

export type ComponentCacheHook<D extends Record<string, any>> = {
    cacheReset: () => void;
    cacheValue: CacheValueFn<D>;
    cacheDump: () => D;
    cacheSave: (saveObject: D) => void;
    cacheSetField: <T>(field: string & keyof D, value: T) => void;
}

// Zustand store for component cache
// Stores arbitrary data keyed by component ID with no restrictions on data types
interface ComponentCacheStore {
    items: Record<string, any>;
    hasKey: (key: string) => boolean;
    setItem: (key: string, value: any) => void;
    setField: (key: string, field: string, value: any) => void;
    resetItem: (key: string) => void;
    getItem: (key: string) => any;
}

const useComponentCacheStore = create<ComponentCacheStore>((set, get) => ({
    items: {},
    hasKey: (key: string) => {
        return get().items[key] !== undefined;
    },
    setItem: (key: string, value: any) => {
        set((state) => ({
            items: {
                ...state.items,
                [key]: value
            }
        }));
    },
    setField: (key: string, field: string, value: any) => {
        set((state) => ({
            items: {
                ...state.items,
                [key]: {
                    ...state.items[key],
                    [field]: value
                }
            }
        }));
    },
    resetItem: (key: string) => {
        set((state) => {
            const {[key]: _, ...rest} = state.items;
            return {items: rest};
        });
    },
    getItem: (key: string) => {
        return get().items[key];
    },
}));

/**
 * Hook for caching component state across mount/unmount cycles.
 *
 * Storage: in-memory Zustand store. Data is per-tab, lost on hard refresh
 * and on closing the tab. There is no persistence layer (no localStorage,
 * no sessionStorage, no IndexedDB).
 *
 * Unlike Redux-based storage, Zustand allows storing any data type including:
 * - Class instances with methods
 * - Objects with circular references
 * - React elements
 * - Functions
 * - Complex nested structures
 *
 * # What to cache
 *
 * The cache's **primary purpose** is to preserve the user's unsaved mid-edit
 * changes across remounts (so navigating away and back doesn't lose work).
 *
 * Cache:
 *  - Transient UI state that should survive a remount inside the same tab:
 *    scroll position, expanded sections, filter inputs, autofocus-already-
 *    ran flags.
 *  - The user's draft of a domain entity WHEN they have unsaved edits
 *    (gated on `hasChanged === true` or equivalent).
 *
 * Do NOT cache:
 *  - **Clean (unmodified) domain entities.** When `hasChanged === false`,
 *    leave `current: Entity` out of the cache so the next mount reads fresh
 *    from Redux. Otherwise the cached snapshot will diverge from server
 *    state when other tabs / Socket.IO sync the entity. (May 2026 Lawson
 *    Legal bug: cached InvoiceCard showed three different totals over 60h
 *    on the same DB state.)
 *  - **Transient UI state owned by a controlled child** like `isMenuOpen`.
 *    Caching it overrides the child's intent on the next mount. (Dec 2024
 *    Select dropdown bug — cached `isMenuOpen: false` immediately closed
 *    menus the user had just opened.)
 *  - Recomputable values (totals, summaries) that depend on data outside
 *    the cache key — recompute on render instead.
 *
 * # API semantics (read these once, then forget them — they're surprising)
 *
 *  - `cacheSave(D)` — **OVERWRITES** the entire cache entry. If you save
 *    `{a:1}` and later `cacheSave({b:2})`, `a` is gone. Use for "save the
 *    whole state on unmount" patterns.
 *  - `cacheSetField(field, value)` — **MERGES** one field into the entry.
 *    `cacheSetField('a', 1)` then `cacheSetField('b', 2)` leaves both
 *    `{a:1, b:2}` in the entry.
 *  - `cacheValue()` — returns the whole entry (or `undefined`).
 *  - `cacheValue(field)` — returns the field value or `undefined`.
 *  - `cacheValue(field, default)` — returns the field value if the field is
 *    present in the entry, else the default. Note: explicitly storing
 *    `undefined` as a field value will return `undefined` from this call,
 *    NOT the default. `in` is the existence check, not value-truthiness.
 *  - `cacheReset()` — deletes the entry entirely.
 *
 * # Keys
 *
 * Cache keys are flat strings; collisions cause data leakage between
 * components. Conventions in use today:
 *  - Functional components: `\`component-name-${id}\`` (kebab-case)
 *  - Class components: `\`ComponentName_${id}\`` (PascalCase + underscore)
 * Stick with one or the other within a single component. The
 * `useComponentCache.keys.test.ts` spec asserts no collisions across files.
 *
 * @example
 *
 *  const {cacheSave, cacheReset, cacheValue} = useComponentCache<TaskState>(`task-item-${id}`);
 *
 *  // Initialize state from cache (or default).
 *  const [task, setTask] = useState(cacheValue('task', null));
 *
 *  // Save the whole state on unmount.
 *  useEffect(() => {
 *      return () => cacheSave({task});
 *  }, [cacheSave, task]);
 *
 *  // Clear cache on OK/Cancel so the next mount starts fresh.
 *  const onOk = () => { cacheReset(); props.onOk?.(); };
 *  const onCancel = () => { cacheReset(); props.onCancel?.(); };
 *
 * @param {string} key - unique key for this cache entry
 * @returns {ComponentCacheHook}
 */
function useComponentCache<D extends object>(key: string): ComponentCacheHook<D> {
    // Get the Zustand store actions
    const setItem = useComponentCacheStore(state => state.setItem);
    const setField = useComponentCacheStore(state => state.setField);
    const resetItem = useComponentCacheStore(state => state.resetItem);
    const getItem = useComponentCacheStore(state => state.getItem);

    // Get the current cached value for this key
    const componentCache = useComponentCacheStore(state => state.items[key]) as D | undefined;

    // Save the entire record (all items)
    const cacheSave = useCallback((item: D) => {
        // Store directly without any cloning or equality checks
        // Zustand handles updates efficiently
        setItem(key, item);
    }, [key, setItem]);

    const cacheSetField = useCallback(<T,>(field: string & keyof D, value: T) => {
        setField(key, field, value);
    }, [key, setField]);

    const cacheReset = useCallback(() => {
        resetItem(key);
    }, [key, resetItem]);

    const cacheDump = useCallback((): D => {
        return getItem(key) as D;
    }, [getItem, key]);

    /**
     * Get the value from the cache.
     *
     * If a field is provided, return the value for that field.
     * If a default value is provided, and the cache does not contain the requested field, return the default value.
     * If no field or default is provided, return the entire cache.
     *
     * @param {string} [field]
     * @param {any} [def]
     */
    const cacheValue: CacheValueFn<D> = useCallback((field?: any, def?: any) => {
        // If no field provided, returns the entire cache.
        // Use `field === undefined` (not `!field`) so that legitimate
        // falsy field names (e.g. '' or 0) still take the lookup path.
        if (field === undefined) return cacheDump();

        // The cache has been created and contains the requested field
        if (componentCache && (field in componentCache)) {
            return componentCache[field as keyof D];
        }

        // The cache does not contain the requested field but default value provided
        if (def !== undefined) return def;

        // No field found and no default provided
        return undefined;
    }, [cacheDump, componentCache]) as CacheValueFn<D>;

    return {cacheSave, cacheReset, cacheValue, cacheDump, cacheSetField};
}

export default useComponentCache;

/**
 * Direct access to component cache store for class components
 * Use these methods when you can't use the hook (e.g., in class components)
 * 
 * @example
 * interface MyState {
 *   taskId: string;
 *   hasChanged: boolean;
 * }
 * 
 * class MyComponent extends React.Component {
 *   private cache = componentCacheStore.typed<MyState>('myKey');
 * 
 *   componentDidMount() {
 *     if (this.cache.hasKey()) {
 *       const state = this.cache.getItem();
 *       this.setState({...state});
 *     }
 *   }
 * 
 *   componentWillUnmount() {
 *     this.cache.setItem(this.state);
 *   }
 * }
 */
// Expose a global function for E2E tests to clear the entire component cache.
// This prevents stale cached editor state from interfering with deep link navigation.
if (typeof window !== 'undefined') {
    (window as any).__clearComponentCache = () => {
        useComponentCacheStore.setState({items: {}});
    };
}

export const componentCacheStore = {
    hasKey: (key: string): boolean => {
        return useComponentCacheStore.getState().hasKey(key);
    },
    setItem: (key: string, value: any) => {
        useComponentCacheStore.getState().setItem(key, value);
    },
    setField: (key: string, field: string, value: any) => {
        useComponentCacheStore.getState().setField(key, field, value);
    },
    resetItem: (key: string) => {
        useComponentCacheStore.getState().resetItem(key);
    },
    getItem: (key: string): any => {
        return useComponentCacheStore.getState().getItem(key);
    },
    
    /**
     * Create a typed interface for the cache store with a specific key
     * Call this once in your component to get type-safe access
     */
    typed: <D extends Record<string, any>>(key: string) => ({
        exists: (): boolean => {
            return useComponentCacheStore.getState().hasKey(key);
        },
        setItem: (value: D) => {
            useComponentCacheStore.getState().setItem(key, value);
        },
        setField: <T extends keyof D>(field: string & T, value: D[T]) => {
            useComponentCacheStore.getState().setField(key, field, value);
        },
        resetItem: () => {
            useComponentCacheStore.getState().resetItem(key);
        },
        getItem: (): D | undefined => {
            return useComponentCacheStore.getState().getItem(key) as D | undefined;
        },
        getField: <T extends keyof D>(field: string & T): D[T] | undefined => {
            const item = useComponentCacheStore.getState().getItem(key) as D | undefined;
            return item?.[field];
        },
        getFieldWithDefault: <T extends keyof D>(field: string & T, defaultValue: D[T]): D[T] => {
            const item = useComponentCacheStore.getState().getItem(key) as D | undefined;
            return item?.[field] ?? defaultValue;
        }
    })
};
